import type React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AdornedInput, Box, FormField, FormHelp, Heading, Label, TextButton } from '@zillow/constellation-10';
import { useGetLandlordPaymentSettingsWebApiHandler_getLandlordPaymentSettings } from '@zg-rentals/ts-rent-payment-client-react-query';

import { centsToDollarsDecimal, dollarsToCents } from '@common/paymentsUtils/currencyUtils';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { validatRentAmount } from './validation';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import userPersonas from '@common/constants/userPersonas';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { savePaymentSettings } from '@rental-manager/actions/OnboardingActions';
import RentTermsModel from '@rental-manager/models/RentTerms';

const RENT_AMOUNT_HELP_TEXT =
  'Enter the rent amount that you want to collect every month. Don’t include prorated rent or move-in costs, such as a security deposit.';

interface Props {
  requestStatus: string;
  monthlyRent: string;
  editMode: boolean;
  onSubmit: (requestPayload: Record<string, unknown>) => any;
  onClose: () => void;
  onBack: () => void;
  onCancel: () => void;
  trackValidationError(tracking: Record<string, unknown>): void;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  propertyId: string;
}

const RentAmountPage: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const {
    currentProperty,
    editMode = false,
    monthlyRent = '',
    onBack,
    onCancel: onCancelProp,
    onClose: onCloseProp,
    onSubmit: submitProp,
    trackValidationError,
    user,
    propertyId,
  } = props;

  const { data, isFetched } = useGetLandlordPaymentSettingsWebApiHandler_getLandlordPaymentSettings(
    {
      propertyId,
    },
    {
      enabled: !!propertyId,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (data?.rentTermsResponseDto) {
      dispatch(
        savePaymentSettings({
          propertyId,
          paymentSettings: RentTermsModel(data.rentTermsResponseDto, 0, {
            dueDate: 0,
            scheduleMonth: '',
            amount: 0,
            fee: 0,
            totalAmount: 0,
            isScheduled: false,
          }),
        }),
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [data]);

  const [value, setValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (monthlyRent && isFetched) {
      const converted = String(centsToDollarsDecimal(parseInt(monthlyRent, 10)));
      setValue(converted);
      setError('');
      setIsLoading(false);
    }
  }, [monthlyRent, isFetched]);

  const gotoPreviousPage = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'rent_amount',
    });
    onBack();
  };

  const onChangeMonthlyRent = (e): void => {
    setValue(e.target.value);
    setError('');
  };

  const handleSubmit = async () => {
    const isValid = validatRentAmount({
      monthlyRentSelected: value,
      trackValidationError,
      setError,
    });

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    const response = await submitProp({
      monthlyRent: dollarsToCents(value),
    });

    if (!(response as any).error) {
      IreneOnboardingCSEvents.emitContinueFromRentAmountPageEvent({ currentProperty, user });
    }

    setIsLoading(false);
  };

  const onCancel = (): void => {
    onCancelProp();
  };

  const onClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'rent_amount',
    });
    onCloseProp();
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={<FaqComponent faqs={FaqConstants.ONBOARDING_V2.RENT_AMOUNT_PAGE} />}
      onRequestClose={onClose}
      header="Payment setup"
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <Heading level={3} textStyle="body-lg-bold">
            How much is the monthly rent?
          </Heading>
          <FormField
            css={{ maxWidth: '36rem' }}
            error={Boolean(error)}
            required
            control={
              <AdornedInput
                fluid
                input={
                  <AdornedInput.Input
                    type="number"
                    className="fs-exclude input-caret-hide"
                    value={value}
                    onChange={onChangeMonthlyRent}
                    onBlur={onChangeMonthlyRent}
                    onFocus={onChangeMonthlyRent}
                  />
                }
                size="md"
                startAdornment={<AdornedInput.Adornment id="start-adornment">$</AdornedInput.Adornment>}
              />
            }
            description={<FormHelp>{error || RENT_AMOUNT_HELP_TEXT}</FormHelp>}
            label={<Label>Monthly rent</Label>}
          />
        </Box>
      }
      backAction={{ onClick: gotoPreviousPage }}
      footerAriaLabel="Rent amount actions"
      footerEnd={
        <>
          {editMode && (
            <TextButton onClick={onCancel} disabled={isLoading}>
              Cancel
            </TextButton>
          )}
          <DebouncedButton onClick={handleSubmit} disabled={isLoading || !value}>
            {editMode ? 'Update' : 'Continue'}
          </DebouncedButton>
        </>
      }
    />
  );
};

export default RentAmountPage;
